import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import cm from 'classnames';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Input } from 'ksnm-common-ui/lib/Input';
import type { ReactElement } from 'react';

import { useOutsideClick } from '@src/utils/hooks';

import type { Route } from '../../AppRouter/types';

import styles from './Search.module.scss';

interface ISearch {
  config: Route[];
}

export const Search = ({ config }: ISearch): ReactElement => {
  const [value, setValue] = useState('');
  const [showHelper, setShowHelper] = useState(false);

  const ref = useRef(null);

  useOutsideClick(
    ref,
    () => setShowHelper(false),
    () => true,
  );

  const items = useMemo(() => {
    const output: Route[] = [];

    const toView = (route: Route[]) => {
      route.forEach((item) => {
        if (item.routes) return toView(item.routes);
        output.push(item);
      });
    };

    toView(config);

    return output.filter((item) => !item.hidden || !item.path);
  }, [config]);

  const isMatch = useMemo(
    () => value && items.some((el) => el.title?.toLowerCase().includes(value.toLowerCase())),
    [items, value],
  );

  return (
    <div className={styles.container} ref={ref}>
      <Input
        onChange={setValue}
        size="small"
        allowClear
        className={cm(styles.search, { [styles.match]: showHelper && isMatch, [styles.focus]: showHelper })}
        value={value}
        onFocus={() => setShowHelper(true)}
        unmask={false}
      />

      {!showHelper && <Icon name="search" className={styles.searchIcon} fill="#547DB1" />}

      {showHelper && isMatch && (
        <div className={styles.helper}>
          {items
            .filter((el) => el.title?.toLowerCase().includes(value.toLowerCase()))
            .map((el) => {
              const index = el.title?.toLowerCase().indexOf(value.toLowerCase()) ?? 0;
              const beforeStr = el.title?.substr(0, index);
              const afterStr = el.title?.substr(index + value.length);
              const selectStr = el.title?.substr(index, value.length);
              const title = (
                <span>
                  {beforeStr}
                  <span className={styles.mark}>{selectStr}</span>
                  {afterStr}
                </span>
              );

              return (
                <Link to={el.path} key={el.path} className={styles.helperItem}>
                  {title}
                </Link>
              );
            })}
        </div>
      )}
    </div>
  );
};
